import React, { useEffect, useMemo, useState } from 'react';
// @ts-ignore — JS module, no type declarations
import { parseStructurizrDsl } from '../../utils/structurizrParser';
// @ts-ignore — JS module, no type declarations
import { ElementFactory } from '../../types/elements';
import ElementRenderer from '../ElementRenderer';
import {
  computeC4Diff, aggregateSubtreeCounts, buildBreadcrumbPath, mapContextIdAcrossVersions,
  arrowKey, ParsedDsl,
} from './c4DiffHelpers';

interface Props {
  fromContent: string;
  toContent: string;
  isDark: boolean;
}

const EMPTY_PARSED: ParsedDsl = { elements: [], elementMap: new Map() };

function safeParse(content: string): ParsedDsl {
  try {
    const r = parseStructurizrDsl(content, ElementFactory) as unknown as ParsedDsl;
    return {
      elements: Array.isArray(r?.elements) ? r.elements : [],
      elementMap: r?.elementMap instanceof Map ? r.elementMap : new Map(),
    };
  } catch {
    return EMPTY_PARSED;
  }
}

const COLOUR = {
  added: '#22c55e',
  removed: '#ef4444',
  changed: '#eab308',
  badge: '#f97316',
} as const;

/** Kinds that are drillable (have children we can show on click). */
const DRILLABLE_KINDS = new Set([
  'system', 'external-system',
  'container', 'webapp', 'database', 'queue', 'mobileapp', 'filestore', 'microservice', 'serverless',
]);

export default function C4DiffViewer({ fromContent, toContent, isDark }: Props) {
  const canvasBg = isDark ? '#181825' : '#f8fafc';
  const noItemsColor = isDark ? '#6c7086' : '#94a3b8';
  const chipBg = isDark ? '#313244' : '#e2e8f0';
  const chipText = isDark ? '#cdd6f4' : '#1e293b';
  const chipActive = isDark ? '#89b4fa' : '#3b82f6';

  const fromParsed = useMemo(() => safeParse(fromContent), [fromContent]);
  const toParsed = useMemo(() => safeParse(toContent), [toContent]);
  const diff = useMemo(() => computeC4Diff(fromParsed, toParsed), [fromParsed, toParsed]);
  const subtreeCounts = useMemo(() => aggregateSubtreeCounts(toParsed, diff), [toParsed, diff]);

  const [currentContextId, setCurrentContextId] = useState<string | null>(null);

  // Fallback: if drill-target disappears (different `to`), reset to root.
  useEffect(() => {
    if (currentContextId && !toParsed.elementMap.has(currentContextId)) {
      setCurrentContextId(null);
    }
  }, [currentContextId, toParsed]);

  // Visible: all `to` elements in current context + removed elements from `from` in equivalent context.
  const visibleElements = useMemo(() => {
    const toVisible = toParsed.elements.filter(
      (e) => (e?.c4?.contextId ?? null) === currentContextId,
    );

    const fromContextId = mapContextIdAcrossVersions(currentContextId, toParsed, fromParsed);
    if (fromContextId === undefined) return toVisible;

    const toVarSet = new Set<string>();
    for (const [, meta] of toParsed.elementMap) toVarSet.add(meta.varName);

    // Removed rectangles (varName gone from `to`) in the equivalent from-side context.
    const removedRects = fromParsed.elements.filter((e) => {
      if (e?.type !== 'rectangle') return false;
      if ((e?.c4?.contextId ?? null) !== fromContextId) return false;
      const meta = fromParsed.elementMap.get(e.id);
      return meta && !toVarSet.has(meta.varName);
    });
    const removedRectIds = new Set(removedRects.map((r: any) => r.id));

    // Removed arrows: arrowKey is in diff.removedArrows AND in this context on the from-side.
    const removedArrows = fromParsed.elements.filter((e) => {
      if (e?.type !== 'arrow') return false;
      if ((e?.c4?.contextId ?? null) !== fromContextId) return false;
      const k = arrowKey(e, fromParsed.elementMap);
      return k !== null && diff.removedArrows.has(k);
    });
    const removedArrowIds = new Set(removedArrows.map((a: any) => a.id));

    // Text attached to removed rects or removed arrows (so labels travel with their owner).
    const removedExtras = fromParsed.elements.filter((e) => {
      if (e?.type !== 'text') return false;
      if (e.attachedToElement && removedRectIds.has(e.attachedToElement)) return true;
      if (e.attachedToArrow && removedArrowIds.has(e.attachedToArrow)) return true;
      return false;
    });

    return [...toVisible, ...removedRects, ...removedArrows, ...removedExtras];
  }, [toParsed, fromParsed, currentContextId, diff]);

  if (visibleElements.length === 0) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100%', color: noItemsColor,
      }}>
        Нет элементов для отображения
      </div>
    );
  }

  // viewBox over all visible elements.
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const el of visibleElements) {
    if (el.type === 'arrow') {
      const pts = el.pathPoints?.length
        ? el.pathPoints
        : [{ x: el.x ?? 0, y: el.y ?? 0 }, { x: el.x2 ?? el.x ?? 0, y: el.y2 ?? el.y ?? 0 }];
      for (const p of pts) {
        minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
      }
    } else if (el.radius != null) {
      minX = Math.min(minX, el.x ?? 0); minY = Math.min(minY, el.y ?? 0);
      maxX = Math.max(maxX, (el.x ?? 0) + el.radius * 2);
      maxY = Math.max(maxY, (el.y ?? 0) + el.radius * 2);
    } else if (el.width != null) {
      minX = Math.min(minX, el.x ?? 0); minY = Math.min(minY, el.y ?? 0);
      maxX = Math.max(maxX, (el.x ?? 0) + el.width);
      maxY = Math.max(maxY, (el.y ?? 0) + (el.height ?? 0));
    } else {
      minX = Math.min(minX, el.x ?? 0); minY = Math.min(minY, el.y ?? 0);
      maxX = Math.max(maxX, (el.x ?? 0) + 80);
      maxY = Math.max(maxY, (el.y ?? 0) + 20);
    }
  }
  const pad = 40;
  const vb = isFinite(minX)
    ? `${minX - pad} ${minY - pad} ${maxX - minX + pad * 2} ${maxY - minY + pad * 2}`
    : '0 0 800 600';

  // Diff colour for rects.
  const toVarByRectId = new Map<string, string>();
  for (const [id, meta] of toParsed.elementMap) toVarByRectId.set(id, meta.varName);
  const fromVarByRectId = new Map<string, string>();
  for (const [id, meta] of fromParsed.elementMap) fromVarByRectId.set(id, meta.varName);

  const rectColour = (rect: any): string | null => {
    if (toVarByRectId.has(rect.id)) {
      const v = toVarByRectId.get(rect.id)!;
      if (diff.added.has(v)) return COLOUR.added;
      if (diff.changed.has(v)) return COLOUR.changed;
      return null;
    }
    if (fromVarByRectId.has(rect.id)) {
      const v = fromVarByRectId.get(rect.id)!;
      if (diff.removed.has(v)) return COLOUR.removed;
    }
    return null;
  };

  // Arrow key lookups for diff colouring.
  const toArrowKeyById = new Map<string, string>();
  for (const e of toParsed.elements) {
    if (e?.type === 'arrow') {
      const k = arrowKey(e, toParsed.elementMap);
      if (k) toArrowKeyById.set(e.id, k);
    }
  }
  const fromArrowKeyById = new Map<string, string>();
  for (const e of fromParsed.elements) {
    if (e?.type === 'arrow') {
      const k = arrowKey(e, fromParsed.elementMap);
      if (k) fromArrowKeyById.set(e.id, k);
    }
  }

  const arrowColour = (arrow: any): string | null => {
    if (toArrowKeyById.has(arrow.id)) {
      const k = toArrowKeyById.get(arrow.id)!;
      if (diff.addedArrows.has(k)) return COLOUR.added;
      if (diff.changedArrows.has(k)) return COLOUR.changed;
      return null;
    }
    if (fromArrowKeyById.has(arrow.id)) {
      const k = fromArrowKeyById.get(arrow.id)!;
      if (diff.removedArrows.has(k)) return COLOUR.removed;
    }
    return null;
  };

  const arrowSvgPath = (arrow: any): string => {
    const pts: Array<{ x: number; y: number }> = arrow.pathPoints?.length
      ? arrow.pathPoints
      : [{ x: arrow.x ?? 0, y: arrow.y ?? 0 }, { x: arrow.x2 ?? arrow.x ?? 0, y: arrow.y2 ?? arrow.y ?? 0 }];
    return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  };

  // ID set: which elements come from the `to` side? Used to split rendering layers.
  const toAllIds = new Set<string>();
  for (const e of toParsed.elements) if (e?.id) toAllIds.add(e.id);

  const svgSafeId = (_origId: string, i: number) => `c4-el-${i}`;

  const breadcrumb = buildBreadcrumbPath(toParsed, currentContextId);

  const isDrillable = (rect: any): boolean => {
    if (!rect?.c4?.kind || rect.c4?.isBoundary) return false;
    if (!DRILLABLE_KINDS.has(rect.c4.kind)) return false;
    return toParsed.elements.some(
      (e) => e?.type === 'rectangle' && (e.c4?.contextId ?? null) === rect.id,
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Breadcrumb */}
      <div style={{
        padding: '8px 12px', borderBottom: `1px solid ${chipBg}`,
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
      }}>
        {breadcrumb.map((seg, idx) => {
          const isLast = idx === breadcrumb.length - 1;
          return (
            <React.Fragment key={seg.id ?? '__root__'}>
              <button
                type="button"
                onClick={() => !isLast && setCurrentContextId(seg.id)}
                disabled={isLast}
                style={{
                  border: 'none', borderRadius: 4, padding: '4px 10px', fontSize: 12,
                  cursor: isLast ? 'default' : 'pointer',
                  background: isLast ? chipActive : chipBg,
                  color: isLast ? '#fff' : chipText,
                }}
              >
                {seg.label}
              </button>
              {!isLast && <span style={{ color: chipText, opacity: 0.6 }}>›</span>}
            </React.Fragment>
          );
        })}
      </div>

      {/* Canvas */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <svg viewBox={vb} style={{ width: '100%', height: '100%', background: canvasBg }}>
          {/* Removed-element layer (from-side only) at 0.45 opacity */}
          <g opacity={0.45}>
            {visibleElements.map((el, i) =>
              !toAllIds.has(el.id) ? (
                <ElementRenderer
                  key={`rm-${el.id}`}
                  element={{ ...el, id: svgSafeId(el.id, i), selected: false } as any}
                  zoom={1}
                  showConnectionPoints={false}
                />
              ) : null,
            )}
          </g>
          {/* Normal layer (to-side only) at full opacity */}
          {visibleElements.map((el, i) =>
            toAllIds.has(el.id) ? (
              <ElementRenderer
                key={el.id}
                element={{ ...el, id: svgSafeId(el.id, i), selected: false } as any}
                zoom={1}
                showConnectionPoints={false}
              />
            ) : null,
          )}

          {/* Diff highlight overlay — rects (border) and arrows (path) */}
          <defs>
            <marker id="c4-arrow-added" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L0,6 L9,3 z" fill={COLOUR.added} />
            </marker>
            <marker id="c4-arrow-removed" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L0,6 L9,3 z" fill={COLOUR.removed} />
            </marker>
            <marker id="c4-arrow-changed" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L0,6 L9,3 z" fill={COLOUR.changed} />
            </marker>
          </defs>
          <g pointerEvents="none">
            {visibleElements.map((el) => {
              if (el.type === 'rectangle') {
                const colour = rectColour(el);
                if (!colour) return null;
                return (
                  <rect
                    key={`hl-${el.id}`}
                    x={(el.x ?? 0) - 3} y={(el.y ?? 0) - 3}
                    width={(el.width ?? 0) + 6} height={(el.height ?? 0) + 6}
                    fill="none" stroke={colour} strokeWidth={2.5}
                    rx={(el.borderRadius ?? 4) + 3} opacity={0.85}
                  />
                );
              }
              if (el.type === 'arrow') {
                const colour = arrowColour(el);
                if (!colour) return null;
                let markerId = '';
                if (colour === COLOUR.added) markerId = 'c4-arrow-added';
                else if (colour === COLOUR.removed) markerId = 'c4-arrow-removed';
                else if (colour === COLOUR.changed) markerId = 'c4-arrow-changed';
                return (
                  <path
                    key={`hl-${el.id}`}
                    d={arrowSvgPath(el)}
                    fill="none" stroke={colour} strokeWidth={3} opacity={0.9}
                    markerEnd={markerId ? `url(#${markerId})` : undefined}
                  />
                );
              }
              return null;
            })}
          </g>

          {/* Nested-change badges + halo on parents whose subtree has diffs */}
          <g pointerEvents="none">
            {visibleElements.map((el) => {
              if (el.type !== 'rectangle') return null;
              if (!toVarByRectId.has(el.id)) return null;
              const count = subtreeCounts.get(el.id) ?? 0;
              if (count <= 0) return null;
              const x = el.x ?? 0;
              const y = el.y ?? 0;
              const w = el.width ?? 0;
              const h = el.height ?? 0;
              const r = 9;
              const bx = x + w - 4;
              const by = y + 4;
              return (
                <g key={`badge-${el.id}`} data-c4-badge="nested-change">
                  {/* halo */}
                  <rect
                    x={x - 4} y={y - 4} width={w + 8} height={h + 8}
                    fill="none" stroke={COLOUR.badge} strokeWidth={2.5}
                    rx={(el.borderRadius ?? 4) + 4} opacity={0.5}
                  />
                  {/* badge circle */}
                  <circle cx={bx} cy={by} r={r} fill={COLOUR.badge} />
                  <text
                    x={bx} y={by + 4}
                    textAnchor="middle"
                    fontSize={11} fontWeight="bold" fill="#ffffff"
                  >
                    {count}
                  </text>
                </g>
              );
            })}
          </g>

          {/* Drill-in click targets — transparent rects on top of drillable rects */}
          {visibleElements.map((el) => {
            if (el.type !== 'rectangle') return null;
            if (!toVarByRectId.has(el.id)) return null;
            if (!isDrillable(el)) return null;
            return (
              <rect
                key={`drill-${el.id}`}
                data-c4-drill-id={el.id}
                data-c4-drill-label={toParsed.elementMap.get(el.id)?.label ?? ''}
                x={el.x ?? 0} y={el.y ?? 0}
                width={el.width ?? 0} height={el.height ?? 0}
                fill="transparent"
                style={{ cursor: 'pointer' }}
                onClick={() => setCurrentContextId(el.id)}
              />
            );
          })}
        </svg>
      </div>
    </div>
  );
}
